import type { ReactNode } from 'react';
import React from 'react';
import { cn } from '@/lib/utils';
import { Icon, type IconName } from '@/components/ui/icon';

interface EntityRowProps {
  /** Primary label */
  title: string;
  /** Secondary label (role, type, etc.) */
  subtitle?: string;
  /** Tertiary info (email, date) */
  meta?: ReactNode;
  /** Icon before title */
  icon?: IconName;
  /** Chips/badges to display */
  badges?: ReactNode;
  /** Click handler */
  onClick?: () => void;
  /** Actions on hover */
  actions?: ReactNode;
  /** Additional class names */
  className?: string;
}

/**
 * List row component for operational lists (personnel, recent items, etc.).
 *
 * Replaces manual `<div className="flex items-center gap-3 px-4 py-3...">` patterns.
 * Use with EntityList for consistent row styling with dividers.
 *
 * Visual treatment:
 * - px-4 py-3, hover:bg-neutral-50 if clickable
 * - Icon, title, subtitle layout
 * - Supports badges and hover actions
 *
 * @example
 * ```tsx
 * <EntityRow
 *   icon="person"
 *   title="Mario Rossi"
 *   subtitle="Inspector"
 *   onClick={() => openPerson(person.id)}
 * />
 *
 * <EntityRow
 *   title="Report #123"
 *   subtitle="15/04/2026"
 *   badges={<Chip tone="warning">Bozza</Chip>}
 *   actions={<Button size="sm" variant="ghost">Edit</Button>}
 * />
 * ```
 */
export function EntityRow({
  title,
  subtitle,
  meta,
  icon,
  badges,
  onClick,
  actions,
  className,
}: EntityRowProps) {
  const isClickable = Boolean(onClick);
  const Comp = isClickable ? 'button' : 'div';

  return (
    <Comp
      type={isClickable ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'group flex items-center gap-3 px-4 py-3 text-left transition-colors',
        isClickable && 'w-full cursor-pointer hover:bg-neutral-50',
        className
      )}
    >
      {icon && (
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-neutral-100 text-neutral-500 transition-colors',
            isClickable && 'group-hover:bg-brand-blue-100 group-hover:text-brand-blue-600'
          )}
        >
          <Icon name={icon} className="h-5 w-5" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-text-primary">{title}</p>
        {subtitle && <p className="truncate text-xs text-text-secondary">{subtitle}</p>}
      </div>
      {meta && <p className="flex-shrink-0 truncate text-xs text-text-tertiary">{meta}</p>}
      {badges && <div className="flex flex-shrink-0 flex-wrap gap-1.5">{badges}</div>}
      {actions && <div className="ml-2 flex-shrink-0">{actions}</div>}
    </Comp>
  );
}

/**
 * Container for EntityRow items with dividers and empty state handling.
 *
 * Visual treatment:
 * - Dividers: divide-y divide-[var(--border-subtle)]
 * - Container: rounded-lg border border-default bg-surface
 * - Empty state: dashed border, centered message
 *
 * @example
 * ```tsx
 * <EntityList emptyMessage="Nessun personale">
 *   {personnel.map(person => (
 *     <EntityRow
 *       key={person.id}
 *       title={person.name}
 *       subtitle={person.role}
 *       icon="person"
 *       onClick={() => openPerson(person.id)}
 *     />
 *   ))}
 * </EntityList>
 * ```
 */
interface EntityListProps {
  /** EntityRow components */
  children: ReactNode;
  /** Message to show when list is empty */
  emptyMessage?: string;
  /** Additional class names */
  className?: string;
}

export function EntityList({ children, emptyMessage, className }: EntityListProps) {
  const hasChildren = React.Children.count(children) > 0;

  if (!hasChildren && emptyMessage) {
    return (
      <div className="rounded-lg border border-dashed border-[var(--border-default)] p-6 text-center text-sm text-text-secondary">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'divide-y divide-[var(--border-subtle)] rounded-lg border border-[var(--border-default)] bg-surface',
        className
      )}
    >
      {children}
    </div>
  );
}
